import React, { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Phone, Calendar, Euro, Award, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatCurrency, formatDateShort, formatDateRange } from '@/utils/format';

/**
 * CustomersView - Display and manage customer records
 * 
 * UPDATED: Now uses apiService for authenticated requests
 */
function CustomersView() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const customersPerPage = 50;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await apiService.get('/admin/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (email) => {
    try {
      const response = await apiService.get(`/admin/customers/${email}`);
      setCustomerDetails(response.data);
      setSelectedCustomer(email);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    }
  };

  const handleClearAllCustomers = async () => {
    setClearing(true);
    try {
      const response = await apiService.delete('/admin/customers/clear/all');
      toast.success(`✅ ${response.data.message}`);
      setShowClearDialog(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear customers');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Customer Database
          </h2>
          <p className="text-sm sm:text-base text-gray-600">{customers.length} customers with booking history</p>
        </div>
        {customers.length > 0 && (
          <Button 
            onClick={() => setShowClearDialog(true)}
            variant="outline"
            size="sm"
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            <span className="hidden sm:inline">Clear All Customers</span>
            <span className="sm:hidden">Clear All</span>
          </Button>
        )}
      </div>

      {/* Customers List */}
      {customers.length === 0 ? (
        <Card>
          <CardContent className="p-8 sm:p-12 text-center">
            <p className="text-sm sm:text-base text-gray-500">No customers yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Customers</div>
                <div className="text-2xl sm:text-3xl font-bold text-blue-600">{customers.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Bookings</div>
                <div className="text-2xl sm:text-3xl font-bold text-green-600">
                  {customers.reduce((sum, c) => sum + c.total_bookings, 0)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Revenue</div>
                <div className="text-2xl sm:text-3xl font-bold text-purple-600">
                  {formatCurrency(customers.reduce((sum, c) => sum + c.total_spent, 0))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customers Table - Desktop */}
          <Card className="border-0 shadow-lg hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total Bookings</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total Spent</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Booking</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {customers
                      .slice((currentPage - 1) * customersPerPage, currentPage * customersPerPage)
                      .map((customer) => (
                      <tr key={customer.email} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{customer.name}</div>
                          {customer.total_bookings >= 5 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Award className="w-4 h-4 text-yellow-500" />
                              <span className="text-xs text-yellow-600 font-medium">VIP Customer</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              {customer.email}
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {customer.phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-lg font-semibold text-blue-600">{customer.total_bookings}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-lg font-semibold text-green-600">{formatCurrency(customer.total_spent)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">
                            {formatDateShort(customer.last_booking)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => fetchCustomerDetails(customer.email)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Customers Cards - Mobile */}
          <div className="md:hidden space-y-3">
            {customers
              .slice((currentPage - 1) * customersPerPage, currentPage * customersPerPage)
              .map((customer) => (
              <Card key={customer.email} className="border shadow-sm">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      {customer.total_bookings >= 5 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Award className="w-3 h-3 text-yellow-500" />
                          <span className="text-xs text-yellow-600 font-medium">VIP</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => fetchCustomerDetails(customer.email)}
                      className="text-blue-600 hover:text-blue-700 text-xs font-medium px-3 py-1 border border-blue-600 rounded-md"
                    >
                      View
                    </button>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      {customer.phone}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t">
                      <div>
                        <div className="text-xs text-gray-500">Bookings</div>
                        <div className="text-sm font-semibold text-blue-600">{customer.total_bookings}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Spent</div>
                        <div className="text-sm font-semibold text-green-600">{formatCurrency(customer.total_spent)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Last</div>
                        <div className="text-xs text-gray-600">{formatDateShort(customer.last_booking).split(' ')[0]}</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Pagination Controls for Customers */}
          {customers.length > customersPerPage && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                Showing {((currentPage - 1) * customersPerPage) + 1} to {Math.min(currentPage * customersPerPage, customers.length)} of {customers.length} customers
              </div>
              <div className="flex gap-1 sm:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.ceil(customers.length / customersPerPage) }, (_, i) => i + 1)
                    .filter(page => {
                      const totalPages = Math.ceil(customers.length / customersPerPage);
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2;
                    })
                    .map((page, idx, arr) => (
                      <React.Fragment key={page}>
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className={currentPage === page ? "bg-blue-600" : ""}
                        >
                          {page}
                        </Button>
                      </React.Fragment>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(customers.length / customersPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(customers.length / customersPerPage)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customer Details Modal */}
      {selectedCustomer && customerDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 sm:p-6 z-10">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{customerDetails.name}</h3>
                  <div className="flex flex-col gap-1 mt-2 text-gray-600 text-sm sm:text-base">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{customerDetails.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      {customerDetails.phone}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Statistics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Bookings</div>
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">{customerDetails.statistics.total_bookings}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Completed</div>
                    <div className="text-xl sm:text-2xl font-bold text-green-600">{customerDetails.statistics.completed_stays}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Cancelled</div>
                    <div className="text-xl sm:text-2xl font-bold text-red-600">{customerDetails.statistics.cancelled_bookings}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1">Spent</div>
                    <div className="text-lg sm:text-xl font-bold text-purple-600">{formatCurrency(customerDetails.statistics.total_spent)}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Booking History */}
              <div>
                <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Booking History</h4>
                <div className="space-y-3">
                  {customerDetails.bookings.map((booking) => (
                    <div key={booking.id} className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                            <span className="font-medium text-sm sm:text-base">
                              {formatDateRange(booking.check_in, booking.check_out)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                            <span>{booking.num_guests} guest{booking.num_guests !== 1 ? 's' : ''}</span>
                            {booking.special_requests && (
                              <span className="italic">"{booking.special_requests.substring(0, 30)}..."</span>
                            )}
                          </div>
                        </div>
                        <div className="flex sm:flex-col sm:items-end justify-between sm:justify-start sm:text-right">
                          <div className="flex items-center gap-1 text-green-600 font-semibold text-sm sm:text-base mb-0 sm:mb-1">
                            <Euro className="w-3 h-3 sm:w-4 sm:h-4" />
                            {formatCurrency(booking.total_amount).replace('€', '')}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            booking.payment_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {booking.payment_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Customers Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Clear All Customers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ Warning: This action cannot be undone!
              </p>
              <p className="text-sm text-red-700">
                This will permanently delete all customer data by removing all bookings from your database.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                💡 <strong>Note:</strong> Customers are derived from bookings. Clearing customers will also clear all bookings.
              </p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowClearDialog(false)}
                disabled={clearing}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-red-600 hover:bg-red-700"
                onClick={handleClearAllCustomers}
                disabled={clearing}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {clearing ? 'Clearing...' : 'Yes, Clear All'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CustomersView;
